import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * 홈 대시보드 좌측 — 실전 퀴즈 배너 2종 (JCG-2110).
 *
 * 이론(amber)/코딩(sky)으로 SubjectBrowser 그룹 색을 그대로 따라가 일관성을 준다.
 * 랜덤 출제 퀴즈 페이지(`/theory/potato-theory-quiz`, `/coding/potato-coding-quiz`)로 이동.
 */
const QUIZZES = [
  {
    href: "/theory/potato-theory-quiz",
    label: "이론 실전 퀴즈",
    desc: "랜덤 출제로 이론 점검",
    img: "/theory/cheer_gamja.webp",
    tone: "theory" as const,
  },
  {
    href: "/coding/potato-coding-quiz",
    label: "코딩 실전 퀴즈",
    desc: "코드 읽기 실전 연습",
    img: "/coding/common/common_gamja.webp",
    tone: "coding" as const,
  },
];

export default function PracticeQuizBanners({
  layout = "stack",
}: {
  layout?: "stack" | "mobile-grid";
}) {
  const isMobileGrid = layout === "mobile-grid";

  return (
    <div className={isMobileGrid ? "grid grid-cols-2 gap-2" : "space-y-2"}>
      {QUIZZES.map((q) => {
        const coding = q.tone === "coding";
        return (
          <Link
            key={q.href}
            href={q.href}
            prefetch={false}
            className={cn(
              "group flex rounded-2xl border p-3 shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 motion-safe:transition-[transform,box-shadow,border-color,background-color] motion-safe:hover:-translate-y-0.5",
              isMobileGrid
                ? "min-h-[116px] flex-col items-start justify-between gap-2"
                : "items-center gap-3",
              coding
                ? "border-sky-200 bg-sky-50/60 focus-visible:ring-sky-500 dark:border-sky-900 dark:bg-sky-950/30"
                : "border-amber-200 bg-amber-50/60 focus-visible:ring-amber-500 dark:border-amber-900 dark:bg-amber-950/30",
            )}
          >
            <Image
              src={q.img}
              alt=""
              width={40}
              height={40}
              className={cn(
                "shrink-0 object-contain transition-transform group-hover:scale-105",
                isMobileGrid ? "h-9 w-9" : "h-10 w-10",
              )}
            />
            <div className={cn("min-w-0", !isMobileGrid && "flex-1")}>
              <p className="text-sm font-bold leading-snug text-foreground">{q.label}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{q.desc}</p>
            </div>
            <ArrowRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5",
                isMobileGrid && "self-end",
                coding ? "text-sky-400" : "text-amber-400",
              )}
              aria-hidden="true"
            />
          </Link>
        );
      })}
    </div>
  );
}
